import { inject, Injectable, signal } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import {
  Auth,
  GoogleAuthProvider,
  User,
  authState,
  signInWithPopup,
  signOut,
} from '@angular/fire/auth';
import { Observable, from, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { ApiClientService } from './api-client.service';
import { AppCheck, getToken as getAppCheckToken } from '@angular/fire/app-check';
import { HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { readStoredToken, setMemToken } from '../shared/utils/session-token';

export interface AuthState {
  loading: boolean;
  error: string | null;
}

export interface LoginResponse {
  idToken: string;
  refreshToken: string;
  expiresIn: string;
  user: {
    uid: string;
    email: string;
    studentProfileId?: string;
  };
}

export interface RegisterStudentResponse {
  idToken: string;
  refreshToken: string;
  expiresIn: string;
  user: {
    uid: string;
    email: string;
    studentProfileId?: string;
  };
}

export interface StudentUser {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string | null;
  studentProfileId?: string;
  pendingDeletion?: boolean;
  profile?: {
    firstName: string;
    lastName: string;
    city: string;
    state: string;
    zip: string;
    danceGenres?: string[];
    subscribeToNewsletter: boolean;
    photoURL?: string | null;
    studios?: { [studioId: string]: { credits: number } };
    studioIds?: string[]; // Deprecated, kept for backward compatibility
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly apiClient = inject(ApiClientService);
  private readonly googleProvider = new GoogleAuthProvider();
  private readonly firebaseApp = inject(FirebaseApp);
  private readonly router = inject(Router);
  // Null on the SSR server and when no site key is configured (see app.config.ts).
  private readonly appCheck = inject(AppCheck, { optional: true });

  // Store ID token and refresh token from backend
  private idToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiresAt: number | null = null;

  // Deduplicates concurrent refresh calls
  private refreshPromise: Promise<string | null> | null = null;

  private readonly IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  // Track current user based on stored token
  private currentUserSubject = new BehaviorSubject<StudentUser | null>(null);
  readonly currentUser$ = this.currentUserSubject.asObservable();
  readonly currentUser = signal<StudentUser | null>(null);

  readonly user$: Observable<User | null> = authState(this.auth);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  
  constructor() {
    this.restoreSessionState();
    this.checkAuthState();
    this.setupActivityTracking();
  }

  private restoreSessionState(): void {
    if (typeof window === 'undefined') return;
    const expiresAt = sessionStorage.getItem('tokenExpiresAt');
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (expiresAt) this.tokenExpiresAt = parseInt(expiresAt, 10);
    if (refreshToken) this.refreshToken = refreshToken;
  }

  /**
   * Store tokens from backend authentication
   */
  private storeTokens(idToken: string, refreshToken: string, expiresIn: string): void {
    this.idToken = idToken;
    this.refreshToken = refreshToken;
    const expiresInSeconds = parseInt(expiresIn, 10) || 3600;
    this.tokenExpiresAt = Date.now() + (expiresInSeconds * 1000);

    // idToken lives in memory only — never written to sessionStorage to reduce XSS exposure.
    setMemToken(idToken, this.tokenExpiresAt);

    if (typeof window !== 'undefined') {
      sessionStorage.setItem('refreshToken', refreshToken);
      sessionStorage.setItem('tokenExpiresAt', this.tokenExpiresAt.toString());
    }
  }

  /**
   * Get stored ID token synchronously. Returns null if expired — callers doing
   * HTTP requests should use getIdTokenAsync() which handles refresh.
   */
  getIdToken(): string | null {
    if (this.tokenExpiresAt && Date.now() >= this.tokenExpiresAt) {
      return null;
    }
    if (this.idToken) return this.idToken;
    const stored = readStoredToken();
    if (stored) {
      this.idToken = stored;
      return stored;
    }
    return null;
  }

  /**
   * Get ID token, automatically refreshing it if expired.
   * Use this for all authenticated API calls.
   */
  async getIdTokenAsync(): Promise<string | null> {
    const current = this.getIdToken();
    if (current) return current;

    const storedRefresh = this.refreshToken ?? (typeof window !== 'undefined' ? sessionStorage.getItem('refreshToken') : null);
    if (!storedRefresh) return null;

    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshAuthToken().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  /**
   * Exchange a Firebase refresh token for a new ID token via the securetoken REST API.
   */
  private async refreshAuthToken(): Promise<string | null> {
    const refreshToken = this.refreshToken ?? (typeof window !== 'undefined' ? sessionStorage.getItem('refreshToken') : null);
    if (!refreshToken) return null;

    const apiKey = this.firebaseApp.options.apiKey;
    if (!apiKey) {
      console.error('Firebase API key not available for token refresh');
      return null;
    }

    try {
      const body = `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`;
      const res = await fetch(
        `https://securetoken.googleapis.com/v1/token?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        }
      );
      if (!res.ok) throw new Error(`Token refresh HTTP error: ${res.status}`);
      const data = await res.json() as { id_token: string; refresh_token: string; expires_in: string };
      this.storeTokens(data.id_token, data.refresh_token, data.expires_in);
      return data.id_token;
    } catch (err) {
      console.error('Token refresh failed:', err);
      this.clearTokens();
      this.clearAuthState();
      return null;
    }
  }
  
  /**
   * Check authentication state and fetch user profile if authenticated
   */
  private async checkAuthState(): Promise<void> {
    const token = await this.getIdTokenAsync();
    if (!token) {
      this.currentUserSubject.next(null);
      this.currentUser.set(null);
      return;
    }
    
    // Fetch user profile from backend
    try {
      const response = await firstValueFrom(
        this.apiClient.get<StudentUser>('/usersstudent/me')
      );
      
      // Map response to StudentUser format
      const user: StudentUser = {
        uid: response.uid,
        email: response.email,
        studentProfileId: response.studentProfileId,
        displayName: response.profile
          ? `${response.profile.firstName} ${response.profile.lastName}`.trim()
          : undefined,
        photoURL: response.profile?.photoURL || response.photoURL || null,
        pendingDeletion: (response as any).pendingDeletion ?? false,
        profile: response.profile,
      };
      
      this.currentUserSubject.next(user);
      this.currentUser.set(user);
    } catch (error) {
      // Token might be invalid, clear auth state
      console.error('Failed to fetch user profile:', error);
      this.clearAuthState();
    }
  }
  
  /**
   * Clear authentication state
   */
  private clearAuthState(): void {
    this.currentUserSubject.next(null);
    this.currentUser.set(null);
  }

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    this.idToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;
    setMemToken(null);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('tokenExpiresAt');
    }
    this.stopIdleTimer();
  }

  private setupActivityTracking(): void {
    if (typeof window === 'undefined') return;
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event =>
      window.addEventListener(event, () => this.resetIdleTimer(), { passive: true })
    );
    this.resetIdleTimer();
  }

  private resetIdleTimer(): void {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.currentUser()) {
        this.signOut()
          .catch(() => {})
          .then(() => this.router.navigate(['/login']));
      }
    }, this.IDLE_TIMEOUT_MS);
  }

  private stopIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      // Call backend login API
      const response = await firstValueFrom(
          this.apiClient.post<LoginResponse>('/usersstudent/login', {
            email: email.trim().toLowerCase(),
            password,
          }),
      );

      // Store ID token from backend - backend has already handled token exchange
      this.storeTokens(response.idToken, response.refreshToken, response.expiresIn);
      
      // Fetch and set user profile
      await this.checkAuthState();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to sign in. Please try again.';
      this.error.set(message);
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * App Check token (reCAPTCHA Enterprise) for sign-up calls, proving the request came from
   * this app rather than a script. Sent without it if unavailable; the backend decides.
   */
  private async appCheckHeaders(): Promise<HttpHeaders | undefined> {
    if (!this.appCheck) return undefined;
    try {
      const { token } = await getAppCheckToken(this.appCheck, false);
      return new HttpHeaders({ 'X-Firebase-AppCheck': token });
    } catch (err) {
      console.warn('App Check token unavailable:', err);
      return undefined;
    }
  }

  async signInWithGoogle(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      // Complete the OAuth flow in the popup
      const result = await signInWithPopup(this.auth, this.googleProvider);

      // Get the Firebase ID token so we can verify it on our backend
      const googleIdToken = await result.user.getIdToken();

      // Exchange the Google ID token for our backend JWT
      const response = await firstValueFrom(
        this.apiClient.post<LoginResponse>('/usersstudent/google-signin', {
          idToken: googleIdToken,
        }, { headers: await this.appCheckHeaders() }),
      );

      // Store tokens and hydrate the current user — same flow as signIn()
      this.storeTokens(response.idToken, response.refreshToken, response.expiresIn);
      await this.checkAuthState();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to sign in with Google. Please try again.';
      this.error.set(message);
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Best-effort: end this device's session on the server so it leaves "Where you're logged
   * in". Capped at a few seconds and never blocks signing out locally.
   */
  private async endServerSession(): Promise<void> {
    if (!this.getIdToken()) return;
    try {
      await Promise.race([
        firstValueFrom(this.apiClient.post('/usersstudent/logout', {})),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch {
      /* already signed out server-side, or offline */
    }
  }

  async signOut(): Promise<void> {
    await this.endServerSession();
    // Clear stored tokens
    this.clearTokens();
    // Clear auth state
    this.clearAuthState();
    // Also sign out from Firebase Auth if there's a session
    if (this.auth.currentUser) {
      await signOut(this.auth);
    }
  }

  logout(): Observable<void> {
    return from(this.signOut());
  }

  async registerStudent(payload: RegisterStudentPayload): Promise<User> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const { email, password, avatarFile, ...profile } = payload;

      // Convert avatar file to base64 if provided
      let avatarFileBase64: string | null = null;
      if (avatarFile) {
        avatarFileBase64 = await this.fileToBase64(avatarFile);
      }

      // Prepare registration data for API
      const registrationData = {
        email: email.trim().toLowerCase(),
        password,
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        city: profile.city.trim(),
        state: profile.state.trim().toUpperCase(),
        zip: profile.zip.trim(),
        phone: profile.phone?.trim() || null,
        danceGenres: profile.danceGenres || [],
        subscribeToNewsletter: profile.subscribeToNewsletter,
        avatarFile: avatarFileBase64,
      };

      // Call backend registration API
      const response = await firstValueFrom(
          this.apiClient.post<RegisterStudentResponse>(
              '/usersstudent/register',
              registrationData,
              { headers: await this.appCheckHeaders() },
          ),
      );

      // Store ID token from backend - backend has already handled token exchange
      this.storeTokens(response.idToken, response.refreshToken, response.expiresIn);
      
      // Fetch and set user profile
      await this.checkAuthState();
      
      // Return a mock user object with the data from the response
      // Since we're not using Firebase Auth state, we'll use the response data
      return {
        uid: response.user.uid,
        email: response.user.email,
      } as User;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to create account. Please try again.';
      this.error.set(message);
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await firstValueFrom(
        this.apiClient.post('/usersstudent/forgot-password', {
          email: email.trim().toLowerCase(),
        })
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to send password reset email. Please try again.';
      throw new Error(message);
    }
  }

  /**
   * Confirm password reset with code from email
   */
  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    try {
      await firstValueFrom(
        this.apiClient.post('/usersstudent/reset-password', {
          oobCode: code,
          newPassword,
        })
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to reset password. Please try again.';
      throw new Error(message);
    }
  }

  /**
   * Change user email address (requires current password)
   */
  async changeEmail(currentPassword: string, newEmail: string): Promise<void> {
    const token = await this.getIdTokenAsync();
    if (!token) {
      throw new Error('No user is currently signed in.');
    }

    try {
      await firstValueFrom(
        this.apiClient.post('/usersstudent/change-email', {
          currentPassword,
          newEmail: newEmail.trim().toLowerCase(),
        })
      );
      // Refresh user profile after email update
      await this.checkAuthState();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to update email address. Please try again.';
      throw new Error(message);
    }
  }

  async requestAccountDeletion(currentPassword: string): Promise<void> {
    const token = await this.getIdTokenAsync();
    if (!token) throw new Error('No user is currently signed in.');
    await firstValueFrom(
      this.apiClient.authenticatedPost('/usersstudent/me/request-deletion', { currentPassword }, token),
    );
  }

  async cancelAccountDeletion(): Promise<void> {
    const token = await this.getIdTokenAsync();
    if (!token) throw new Error('No user is currently signed in.');
    await firstValueFrom(
      this.apiClient.authenticatedDelete('/usersstudent/me/cancel-deletion', token),
    );
  }

  /**
   * Convert File to base64 string
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

}

export interface RegisterStudentPayload {
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  zip: string;
  phone?: string | null;
  email: string;
  password: string;
  subscribeToNewsletter: boolean;
  danceGenres?: DanceGenre[];
  avatarFile?: File | null;
}

export type DanceGenre =
  // Latin
  | 'salsa' | 'bachata' | 'merengue' | 'cumbia' | 'cha-cha' | 'reggaeton'
  // African / Brazilian
  | 'zouk' | 'kizomba' | 'semba' | 'afrobeats' | 'samba'
  // Ballroom
  | 'waltz' | 'tango' | 'argentine tango' | 'foxtrot' | 'quickstep' | 'viennese waltz'
  // Swing
  | 'west coast swing' | 'lindy hop' | 'east coast swing'
  // Urban / Street
  | 'hip-hop' | 'breakdance' | 'house' | 'waacking' | 'voguing' | 'popping & locking'
  // Contemporary / Theater
  | 'contemporary' | 'jazz' | 'modern' | 'lyrical'
  // Classical / Other
  | 'ballet' | 'tap' | 'flamenco' | 'belly dance';

export interface StudentProfile {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  zip: string;
  danceGenres: DanceGenre[];
  subscribeToNewsletter: boolean;
  role: 'student';
  photoURL: string | null;
  createdAt: unknown;
  updatedAt: unknown;
}

